import React, { useState, useEffect } from "react";
import { MessageResource } from "../../../resources";
import { Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import {
	MyPageButton,
	MyPagePaper,
	SnackbarContentWrapper,
} from "../../common";
import { FormattedMessage } from "react-intl";
import clsx from "clsx";
import { useCommonWidths } from "../../../hooks";
import GetAppIcon from "@material-ui/icons/GetApp";
import { navigate } from "@reach/router";
import { useSnackbar, OptionsObject } from "notistack";
import { useFetcher } from "rest-hooks";
import { MessageModel } from "../../../resources/MessageResource";
import { downloadDocument } from "../../common/DownloadDocumentButton";

interface MessageViewProps {
	activeMessageIndex: number;
	filteredMessages: MessageModel[];
	setFilteredMessages: (messages: MessageModel[]) => void;
	onReturn: (x: number) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
	greyText: {
		color: "grey",
	},
	boldText: {
		fontWeight: "bold",
	},
	messageBody: {
		marginTop: theme.spacing(2),
	},
	messageNavButtons: {
		marginTop: 15,
	},
}));

const MessageView = (props: MessageViewProps) => {
	const {
		activeMessageIndex,
		filteredMessages,
		setFilteredMessages,
		onReturn,
	} = props;
	const { enqueueSnackbar } = useSnackbar();

	const [index, setIndex] = useState(activeMessageIndex);
	const [messageData, setMessageData] = useState(
		filteredMessages[activeMessageIndex]
	);

	const updateReadMessage = useFetcher(MessageResource.partialUpdateShape());

	useEffect(() => {
		if (messageData.opened) return;
		setMessageData({ ...messageData, opened: true } as MessageModel);

		const newObj = MessageResource.fromClientModel(messageData);

		updateReadMessage({ id: messageData.id }, newObj).then(() => {
			setFilteredMessages(
				filteredMessages.map((fm) => {
					if (fm.id === messageData.id) {
						return { ...messageData, opened: true } as MessageModel;
					}
					return fm;
				})
			);
		});
	}, [
		filteredMessages,
		messageData,
		messageData.opened,
		setFilteredMessages,
		updateReadMessage,
	]);

	const classes = useStyles();
	const isMobile = useCommonWidths().isSmDown;

	return (
		<>
			<MyPagePaper>
				<MyPageButton onClick={() => onReturn(-1)} backIcon>
					<FormattedMessage
						id="minside.messages.messageveiw.arrow_back_label"
						defaultMessage="Meldinger"
						description="Label for back to messages button"
					/>
				</MyPageButton>
				<Typography variant="body2" className={classes.greyText}>
					{messageData.objectCar}
				</Typography>
				<Typography
					variant={isMobile ? "h6" : "h5"}
					className={classes.boldText}
				>
					{messageData.title}
				</Typography>
				{messageData.receivedDateDisplay && (
					<Typography variant="caption" className={classes.greyText}>
						{messageData.receivedDateDisplay}
					</Typography>
				)}
				<Typography
					variant="body2"
					className={clsx(classes.boldText, classes.messageBody)}
				>
					{messageData.messageBody}
				</Typography>

				<Grid container direction="column">
					{messageData.attachments &&
						messageData.attachments.map((attachment) => {
							return (
								<Grid key={attachment.id} item>
									<MyPageButton
										onClick={() =>
											downloadDocument({
												endpoint: "/api/documents/" + attachment.id,
												filename: attachment.name,
												setLoading: () => {},
												setResponseCode: () => {},
												enqueueSnackbar: enqueueSnackbar,
											})
										}
									>
										<GetAppIcon style={{ marginRight: 10 }} />
										<Typography>{attachment.name}</Typography>
									</MyPageButton>
								</Grid>
							);
						})}
				</Grid>
			</MyPagePaper>
			<Grid spacing={1} container direction="row">
				<Grid item>
					<MyPageButton
						className={classes.messageNavButtons}
						onClick={() => {
							setMessageData(filteredMessages[index - 1]);
							setIndex(index - 1);
						}}
						disabled={index <= 0}
						backIcon="chevron"
						buttonVariant="outlined"
					>
						<FormattedMessage
							id="minside.messages.messageveiw.last_message"
							defaultMessage="Forrige melding"
							description="Label for last message button"
						/>
					</MyPageButton>
				</Grid>
				<Grid item>
					<MyPageButton
						className={classes.messageNavButtons}
						onClick={() => {
							setMessageData(filteredMessages[index + 1]);
							setIndex(index + 1);
						}}
						disabled={index >= filteredMessages.length - 1}
						nextIcon="chevron"
						buttonVariant="outlined"
					>
						<FormattedMessage
							id="minside.messages.messageveiw.next_message"
							defaultMessage="Neste melding"
							description="Label for next message button"
						/>
					</MyPageButton>
				</Grid>
			</Grid>
		</>
	);
};

export default MessageView;
